/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as vscode from 'vscode';
import { DiagnosticLanguage } from '../configuration/languageDescription';
import * as arrays from '../utils/arrays';
import { $whc } from '../utils/dispose';
import { $_hc } from '../utils/resourceMap';

function diagnosticsEquals(a: vscode.Diagnostic, b: vscode.Diagnostic): boolean {
	if (a === b) {
		return true;
	}

	return a.code === b.code
		&& a.message === b.message
		&& a.severity === b.severity
		&& a.source === b.source
		&& a.range.isEqual(b.range)
		&& arrays.$thc(a.relatedInformation || arrays.$shc, b.relatedInformation || arrays.$shc, (a, b) => {
			return a.message === b.message
				&& a.location.range.isEqual(b.location.range)
				&& a.location.uri.fsPath === b.location.uri.fsPath;
		})
		&& arrays.$thc(a.tags || arrays.$shc, b.tags || arrays.$shc);
}

export const enum DiagnosticKind {
	Syntax,
	Semantic,
	Suggestion,
}

class FileDiagnostics {

	private readonly c = new Map<DiagnosticKind, ReadonlyArray<vscode.Diagnostic>>();

	constructor(
		public readonly file: vscode.Uri,
		public language: DiagnosticLanguage
	) { }

	public updateDiagnostics(
		language: DiagnosticLanguage,
		kind: DiagnosticKind,
		diagnostics: ReadonlyArray<vscode.Diagnostic>
	): boolean {
		if (language !== this.language) {
			this.c.clear();
			this.language = language;
		}

		const existing = this.c.get(kind);
		if (existing?.length === 0 && diagnostics.length === 0) {
			// No need to update
			return false;
		}

		this.c.set(kind, diagnostics);
		return true;
	}

	public getAllDiagnostics(settings: DiagnosticSettings): vscode.Diagnostic[] {
		if (!settings.getValidate(this.language)) {
			return [];
		}

		return [
			...this.e(DiagnosticKind.Syntax),
			...this.e(DiagnosticKind.Semantic),
			...this.d(settings),
		];
	}

	public delete(toDelete: vscode.Diagnostic): void {
		for (const [type, diags] of this.c) {
			this.c.set(type, diags.filter(diag => !diagnosticsEquals(diag, toDelete)));
		}
	}

	private d(settings: DiagnosticSettings) {
		const enableSuggestions = settings.getEnableSuggestions(this.language);
		return this.e(DiagnosticKind.Suggestion).filter(x => {
			if (!enableSuggestions) {
				// Still show unused
				return x.tags && (x.tags.includes(vscode.DiagnosticTag.Unnecessary) || x.tags.includes(vscode.DiagnosticTag.Deprecated));
			}
			return true;
		});
	}

	private e(kind: DiagnosticKind): ReadonlyArray<vscode.Diagnostic> {
		return this.c.get(kind) || [];
	}
}

interface LanguageDiagnosticSettings {
	readonly validate: boolean;
	readonly enableSuggestions: boolean;
}

function areLanguageDiagnosticSettingsEqual(currentSettings: LanguageDiagnosticSettings, newSettings: LanguageDiagnosticSettings): boolean {
	return currentSettings.validate === newSettings.validate
		&& currentSettings.enableSuggestions === newSettings.enableSuggestions;
}

class DiagnosticSettings {
	private static readonly c: LanguageDiagnosticSettings = {
		validate: true,
		enableSuggestions: true
	};

	private readonly d = new Map<DiagnosticLanguage, LanguageDiagnosticSettings>();

	public getValidate(language: DiagnosticLanguage): boolean {
		return this.e(language).validate;
	}

	public setValidate(language: DiagnosticLanguage, value: boolean): boolean {
		return this.g(language, settings => ({
			validate: value,
			enableSuggestions: settings.enableSuggestions,
		}));
	}

	public getEnableSuggestions(language: DiagnosticLanguage): boolean {
		return this.e(language).enableSuggestions;
	}

	public setEnableSuggestions(language: DiagnosticLanguage, value: boolean): boolean {
		return this.g(language, settings => ({
			validate: settings.validate,
			enableSuggestions: value
		}));
	}

	private e(language: DiagnosticLanguage): LanguageDiagnosticSettings {
		return this.d.get(language) || DiagnosticSettings.c;
	}

	private g(language: DiagnosticLanguage, f: (x: LanguageDiagnosticSettings) => LanguageDiagnosticSettings): boolean {
		const currentSettings = this.e(language);
		const newSettings = f(currentSettings);
		this.d.set(language, newSettings);
		return !areLanguageDiagnosticSettingsEqual(currentSettings, newSettings);
	}
}

export class $aic extends $whc {
	private readonly e: $_hc<FileDiagnostics>;
	private readonly i = new DiagnosticSettings();
	private readonly j: vscode.DiagnosticCollection;
	private readonly k: $_hc<any>;

	private readonly l = 50;

	constructor(
		owner: string,
		onCaseInsensitiveFileSystem: boolean
	) {
		super();
		this.e = new $_hc<FileDiagnostics>(undefined, { onCaseInsensitiveFileSystem });
		this.k = new $_hc<any>(undefined, { onCaseInsensitiveFileSystem });

		this.j = this.g(vscode.languages.createDiagnosticCollection(owner));
	}

	public override dispose() {
		super.dispose();

		for (const value of this.k.values()) {
			clearTimeout(value);
		}
		this.k.clear();
	}

	public reInitialize(): void {
		this.j.clear();
		this.e.clear();
	}

	public setValidate(language: DiagnosticLanguage, value: boolean) {
		const didUpdate = this.i.setValidate(language, value);
		if (didUpdate) {
			this.o();
		}
	}

	public setEnableSuggestions(language: DiagnosticLanguage, value: boolean) {
		const didUpdate = this.i.setEnableSuggestions(language, value);
		if (didUpdate) {
			this.o();
		}
	}

	public updateDiagnostics(
		file: vscode.Uri,
		language: DiagnosticLanguage,
		kind: DiagnosticKind,
		diagnostics: ReadonlyArray<vscode.Diagnostic>
	): void {
		let didUpdate = false;
		const entry = this.e.get(file);
		if (entry) {
			didUpdate = entry.updateDiagnostics(language, kind, diagnostics);
		} else if (diagnostics.length) {
			const fileDiagnostics = new FileDiagnostics(file, language);
			fileDiagnostics.updateDiagnostics(language, kind, diagnostics);
			this.e.set(file, fileDiagnostics);
			didUpdate = true;
		}

		if (didUpdate) {
			this.m(file);
		}
	}

	public configFileDiagnosticsReceived(
		file: vscode.Uri,
		diagnostics: ReadonlyArray<vscode.Diagnostic>
	): void {
		this.j.set(file, diagnostics);
	}

	public deleteAllDiagnosticsInFile(resource: vscode.Uri): void {
		this.j.delete(resource);
		this.e.delete(resource);
	}

	public deleteDiagnostic(resource: vscode.Uri, diagnostic: vscode.Diagnostic): void {
		const fileDiagnostics = this.e.get(resource);
		if (fileDiagnostics) {
			fileDiagnostics.delete(diagnostic);
			this.p(fileDiagnostics);
		}
	}

	public getDiagnostics(file: vscode.Uri): ReadonlyArray<vscode.Diagnostic> {
		return this.j.get(file) || [];
	}

	private m(file: vscode.Uri) {
		if (!this.k.has(file)) {
			this.k.set(file, setTimeout(() => this.n(file), this.l));
		}
	}

	private n(file: vscode.Uri): void {
		if (this.k.has(file)) {
			clearTimeout(this.k.get(file));
			this.k.delete(file);
		}

		const fileDiagnostics = this.e.get(file);
		this.j.set(file, fileDiagnostics ? fileDiagnostics.getAllDiagnostics(this.i) : []);
	}

	private o(): void {
		this.j.clear();
		for (const fileDiagnostic of this.e.values()) {
			this.p(fileDiagnostic);
		}
	}

	private p(fileDiagnostic: FileDiagnostics) {
		this.j.set(fileDiagnostic.file, fileDiagnostic.getAllDiagnostics(this.i));
	}
}
